/**
 * Status System — single source of truth (CT-4195).
 *
 * Every status-displaying surface (Provider Header, Instance Header, list rows,
 * Already Integrated cards, the Needs Your Attention band) renders from these
 * tokens — there is NO per-surface color/label logic. Status is pre-computed
 * server-side (mocked on each entity's `status` field in V1); the frontend only
 * renders it. Derivation rules are the open engineering definition on ARP-792.
 *
 * Unified, reason-as-state taxonomy: 8 core states + 3 edge states → 3 buckets.
 */

/** The 11 canonical status keys. */
export const INTEGRATION_STATUS = {
  WORKING: 'working',
  AUTH_EXPIRING: 'auth_expiring',
  RATE_LIMITED: 'rate_limited',
  SCHEMA_DRIFT: 'schema_drift',
  NEEDS_MAPPING: 'needs_mapping',
  AUTH_FAILED: 'auth_failed',
  AUTH_EXPIRED: 'auth_expired',
  PROVIDER_DOWN: 'provider_down',
  DISCONNECTED: 'disconnected',
  UNKNOWN: 'unknown',
  NOT_CONNECTED: 'not_connected',
};

/** The three buckets a status rolls up into. */
export const STATUS_BUCKET = {
  WORKING: 'working',
  NEEDS_ATTENTION: 'needs_attention',
  DISCONNECTED: 'disconnected',
};

/** state → bucket. `not_connected` has no bucket (renders a Connect CTA, no pill). */
export const STATE_TO_BUCKET = {
  working: STATUS_BUCKET.WORKING,
  auth_expiring: STATUS_BUCKET.NEEDS_ATTENTION,
  rate_limited: STATUS_BUCKET.NEEDS_ATTENTION,
  schema_drift: STATUS_BUCKET.NEEDS_ATTENTION,
  needs_mapping: STATUS_BUCKET.NEEDS_ATTENTION,
  auth_failed: STATUS_BUCKET.NEEDS_ATTENTION,
  auth_expired: STATUS_BUCKET.NEEDS_ATTENTION,
  provider_down: STATUS_BUCKET.NEEDS_ATTENTION,
  disconnected: STATUS_BUCKET.DISCONNECTED,
  unknown: STATUS_BUCKET.DISCONNECTED,
};

/** Tier within needs_attention: amber (warn) vs red (broken now). */
export const STATUS_TIER = {
  auth_expiring: 'amber',
  rate_limited: 'amber',
  schema_drift: 'amber',
  needs_mapping: 'amber',
  auth_failed: 'red',
  auth_expired: 'red',
  provider_down: 'red',
};

/** state → @aimsos/ui Badge variant (no hardcoded colors anywhere else). */
export const STATUS_BADGE_VARIANT = {
  working: 'success',
  auth_expiring: 'alert',
  rate_limited: 'warning',
  schema_drift: 'warning',
  needs_mapping: 'warning',
  auth_failed: 'danger',
  auth_expired: 'danger',
  provider_down: 'danger',
  disconnected: 'neutral',
  unknown: 'neutral',
};

/**
 * Severity order (highest → lowest) for the attention band and status sorting.
 * The single definition — `attentionSeverity.js` re-exports this.
 */
export const SEVERITY_ORDER = [
  'auth_failed',
  'auth_expired',
  'provider_down',
  'rate_limited',
  'schema_drift',
  'needs_mapping',
  'auth_expiring',
];

/** reason → CTA id, consumed by the band (CT-4159) and banners (CT-4198). */
export const STATUS_CTA = {
  auth_failed: 'reauthenticate',
  auth_expired: 'reauthenticate',
  auth_expiring: 'rotate_credentials',
  provider_down: 'view_logs',
  rate_limited: 'view_usage',
  schema_drift: 'resolve_drift',
  needs_mapping: 'map_fields',
};

/**
 * CTA id (from `STATUS_CTA`) → i18n label key in the `admin-studio` namespace.
 * Single source for every surface rendering a contextual CTA (the attention
 * band CT-4159, the connected card CT-4161) — never redefine this map locally.
 */
export const CTA_LABEL_KEY = {
  reauthenticate: 'integrations.attention.cta.reauthenticate',
  rotate_credentials: 'integrations.attention.cta.rotateCredentials',
  view_logs: 'integrations.attention.cta.viewLogs',
  view_usage: 'integrations.attention.cta.viewUsage',
  resolve_drift: 'integrations.attention.cta.resolveDrift',
  map_fields: 'integrations.attention.cta.mapFields',
};

/** state → dot background class (single source; used by the Marketplace active strip health dots). */
export const STATUS_DOT_CLASS = {
  working: 'bg-emerald-500',
  auth_expiring: 'bg-amber-500',
  rate_limited: 'bg-amber-500',
  schema_drift: 'bg-amber-500',
  needs_mapping: 'bg-amber-500',
  auth_failed: 'bg-red-500',
  auth_expired: 'bg-red-500',
  provider_down: 'bg-red-500',
  disconnected: 'bg-gray-400',
  unknown: 'bg-gray-400',
};

/** i18n key for a status label (in the `admin-studio` namespace). */
export const statusLabelKey = (state) => `integrations.status.${state ?? 'unknown'}`;

/** i18n key for the recency-based "Active" label variant of `working`. */
export const WORKING_ACTIVE_LABEL_KEY = 'integrations.status.working_active';

/** Lower index = more severe; unknown states sort last. */
export const severityRank = (state) => {
  const index = SEVERITY_ORDER.indexOf(state);
  return index === -1 ? SEVERITY_ORDER.length : index;
};
